import './index.css'
import cn from 'clsx'
import debounce from 'lodash/debounce'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactMaskedInput from 'react-maskedinput'
import { useSelector } from 'react-redux'
import scrollIntoView from 'scroll-into-view-if-needed'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { convertStringToURL } from '@disco/utils/links'
import Button from 'components/ui/button'
import FocusWithin from 'components/ui/focus-within'
import InlineError from 'components/ui/inline-error'
import Label from 'components/ui/label'
import Tooltip from 'components/ui/tooltip'

const stopPropagation = (event: Event) => event.stopPropagation()

const scrollToElement = debounce((element: HTMLElement) => {
  const intersectionObserver = new IntersectionObserver(
    function (entries) {
      // If intersectionRatio is 0, the target is out of view
      // and we need to do scroll.
      if (entries[0].intersectionRatio <= 0) {
        try {
          scrollIntoView(element, {
            behavior: 'smooth',
            scrollMode: 'if-needed',
          })
          intersectionObserver.unobserve(element)
        } catch (error) {
          console.log(error)
        }
      }
    },
    {
      root: element,
    },
  )
  intersectionObserver.observe(element)
}, 300)
type Props = {
  autoComplete?: string
  autoFocus?: boolean
  compact?: boolean
  defaultValue?: string
  disabled?: boolean
  error?: React.ReactNode | React.ReactNode[]
  // TODO: Rename to nodeLeft
  icon?: React.ReactNode
  id?: string
  inputRef?:
    | React.RefCallback<HTMLInputElement>
    | React.MutableRefObject<HTMLInputElement>
  invalid?: boolean
  label?: React.ReactNode
  loading?: boolean
  multiline?: boolean | number
  maxLength?: number
  maskInput?: string | number
  name?: string
  nodeRight?: React.ReactNode
  pattern?: string
  patternErrorMessage?: string | null | undefined
  placeholder?: string
  readOnly?: boolean
  required?: boolean
  tooltipId?: string
  type?: 'password' | 'url' | string
  value?: string
  warning?: boolean
  hidden?: boolean
  onBlur?: (event: any) => void
  onChange?: (event: any) => void
  onFocus?: () => void
  onKeyPress?: (event: any) => void
  onPaste?: (event: any) => void
  onInvalid?: (event: any) => void
  textAlign?: 'center'
}

/**
 * @deprecated This component should be extracted to components/ds following the SearchTextField/PasswordTextField pattern.
 * @see {@link components/ds/TextField}
 * @see {@link components/ds/TextArea}
 * @see {@link components/ds/PasswordTextField}
 * @see {@link components/ds/SearchTextField}
 * @migration
 * - This component provides comprehensive form field functionality (label, validation, errors, loading, masking)
 * - Instead of asking consumers to manually compose TextField with all these features, extract to a reusable DS component
 * - Suggested approach: Create `FormField` or `FormTextField` in components/ds that wraps TextField with these features:
 *   - label prop → Renders label using `<Text as="label">`
 *   - required prop → Shows asterisk in label
 *   - error prop → Renders error messages below the field
 *   - invalid prop → Applies error styling to TextField
 *   - icon prop → Renders icon in `<TextField.Slot side="left">`
 *   - nodeRight prop → Renders content in `<TextField.Slot side="right">`
 *   - loading prop → Renders Spinner in TextField.Slot
 *   - multiline prop → Switches to TextArea component
 *   - Keep the existing prop API for easier migration
 * - Follow the pattern established by SearchTextField and PasswordTextField
 * - Complex features may need additional consideration:
 *   - maskInput → Consider if masking should be part of the DS component or use a form library
 *   - pattern/patternErrorMessage → May integrate with form validation libraries
 *   - type="url" auto-formatting → Can be included in the DS component
 *   - scrollIntoView on invalid → Can be included as optional behavior
 * - Once created, update imports throughout the codebase to use the new DS-based version
 * - For password inputs specifically, use `PasswordTextField` from components/ds
 * - For search inputs specifically, use `SearchTextField` from components/ds
 *
 * A comprehensive text field component with support for various input types, validation, and styling
 * @param autoComplete - HTML autocomplete attribute
 * @param autoFocus - Whether to automatically focus the input
 * @param compact - Whether to use compact styling (defaults to true)
 * @param defaultValue - Default value for the input
 * @param disabled - Whether the input is disabled
 * @param error - Error message or messages to display
 * @param icon - Optional icon to display on the left side
 * @param id - HTML id attribute
 * @param inputRef - Ref object or callback for the input element
 * @param invalid - Whether the input is in an invalid state
 * @param label - Optional label text
 * @param loading - Whether the input is in a loading state
 * @param multiline - Whether to render as textarea (true) or number of rows
 * @param maxLength - Maximum character length
 * @param maskInput - Input mask pattern
 * @param name - HTML name attribute
 * @param nodeRight - Optional content to display on the right side
 * @param pattern - HTML pattern attribute for validation
 * @param patternErrorMessage - Error message for pattern validation
 * @param placeholder - Placeholder text
 * @param readOnly - Whether the input is read-only
 * @param required - Whether the input is required
 * @param tooltipId - ID for tooltip association
 * @param type - Input type (password, url, or other HTML input types)
 * @param value - Controlled value
 * @param warning - Whether to show warning state
 * @param onBlur - Callback for blur events
 * @param onChange - Callback for value changes
 * @param onFocus - Callback for focus events
 * @param onKeyPress - Callback for key press events
 * @param onPaste - Callback for paste events
 * @param onInvalid - Callback for invalid events
 * @param hidden - Whether the field is hidden
 * @param textAlign - Text alignment within the input
 * @returns A comprehensive text field component with validation and styling
 */
const TextField = ({
  autoComplete,
  autoFocus,
  compact = true,
  defaultValue,
  disabled,
  error,
  icon,
  id,
  inputRef,
  invalid,
  label,
  loading,
  multiline,
  maxLength,
  maskInput,
  name,
  nodeRight,
  pattern,
  patternErrorMessage,
  placeholder,
  readOnly,
  required,
  tooltipId,
  type,
  value,
  warning,
  onBlur,
  onChange,
  onFocus,
  onKeyPress,
  onPaste,
  onInvalid,
  hidden,
  textAlign,
}: Props): React.ReactNode => {
  const fieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const isPhone = useSelector(isDeviceTypePhone)
  const isCompact = isPhone ? false : compact
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const [isMasked, setIsMasked] = useState<boolean>(true)
  const [patternErrorMessageState, setPatternErrorMessageState] = useState<
    string | null | undefined
  >(null)
  useEffect(() => {
    if (autoFocus && fieldRef.current) {
      fieldRef.current.focus()
    }
  }, [autoFocus, disabled])
  useEffect(() => {
    if (inputRef) {
      if ('current' in inputRef) {
        // eslint-disable-next-line no-param-reassign
        inputRef.current = fieldRef.current as HTMLInputElement
      } else {
        inputRef(fieldRef.current as HTMLInputElement)
      }
    }
  }, [inputRef])
  const handleBlur = useCallback(
    (e: any) => {
      let event = e
      setIsFocused(false)

      if (onBlur) {
        onBlur(event)
      }

      if (type === 'url' && onChange) {
        const url = convertStringToURL(event.target.value)
        event = {
          ...event,
          target: {
            ...event.target,
            value: url.host ? (url.protocol || 'https:') + '//' + url.host : '',
          },
        }
        onChange(event)
      }
    },
    [onBlur, onChange, type],
  )
  const handleChange = useCallback(
    (e: any) => {
      let event = e

      if (multiline) {
        updateHeight()
      }

      if (type === 'url') {
        const url = convertStringToURL(event.target.value)
        event = { ...event, target: { ...event.target, value: url.href } }
      }

      if (onChange) {
        onChange(event)
      }

      setPatternErrorMessageState(state => (state ? '' : state))
    },
    [multiline, onChange, type],
  )
  const handleKeyPress = useCallback(
    (e: any) => {
      if (onKeyPress) {
        onKeyPress(e)
      }
    },
    [onKeyPress],
  )
  const handleFocus = useCallback(() => {
    setIsFocused(true)

    if (onFocus) {
      onFocus()
    }
  }, [onFocus])
  const toggleMask = useCallback(() => {
    setIsMasked(isMasked => !isMasked)
  }, [])

  const updateHeight = () => {
    if (containerRef.current && fieldRef.current) {
      fieldRef.current.style.height = 'auto'
      const height = `${fieldRef.current.scrollHeight}px`
      containerRef.current.style.height = height
      fieldRef.current.style.height = height
    }
  }

  useEffect(() => {
    if (multiline) {
      updateHeight()
    }
  }, [multiline])
  useEffect(() => {
    if (patternErrorMessageState) {
      setPatternErrorMessageState('')
    }
  }, [value])
  let Element: React.ElementType = multiline ? 'textarea' : 'input'

  if (maskInput) {
    Element = ReactMaskedInput
  }

  const minRows = typeof multiline === 'number' ? multiline : 3
  let inputType

  if (type === 'password') {
    inputType = isMasked ? 'password' : 'text'
  } else {
    inputType = type
  }

  return (
    <FocusWithin onBlur={handleBlur} onFocus={handleFocus}>
      {label && (
        <Label caption={label} required={required} tooltipId={tooltipId} />
      )}
      <div
        className={cn('TextField', {
          'TextField--disabled': disabled,
          'TextField--focused': isFocused,
          'TextField--invalid': invalid || patternErrorMessageState,
          'TextField--loading': loading,
          'TextField--warning': warning,
          'TextField--hidden': hidden,
        })}
      >
        {icon && <div className="TextField-icon">{icon}</div>}
        <div className="TextField-input-wrapper" ref={containerRef}>
          <Element
            className={cn('TextField-input', {
              'TextField-input--compact': isCompact,
              'TextField-input--default': !isCompact,
              'TextField-input--disabled': disabled || loading,
              'TextField-input--focused': isFocused,
              'TextField-input--textCenter': textAlign === 'center',
            })}
            style={{
              textAlign,
            }}
            autoComplete={autoComplete}
            defaultValue={defaultValue}
            disabled={disabled || loading}
            id={id}
            maxLength={maxLength}
            mask={maskInput}
            name={name}
            pattern={pattern}
            placeholder={placeholder}
            readOnly={readOnly}
            ref={fieldRef as React.Ref<HTMLInputElement & HTMLTextAreaElement>}
            required={required}
            rows={multiline && minRows}
            type={inputType}
            value={value}
            onChange={handleChange}
            onKeyDown={stopPropagation}
            onKeyPress={handleKeyPress}
            onPaste={onPaste}
            onInvalid={
              onInvalid
                ? event => {
                    event.preventDefault()
                    setPatternErrorMessageState(patternErrorMessage)

                    if (onInvalid) {
                      onInvalid(event)
                    }

                    if (fieldRef.current) {
                      scrollToElement(fieldRef.current)
                    }
                  }
                : undefined
            }
          />
        </div>
        {nodeRight && (
          <div
            className={cn('TextField-node-right', {
              'TextField-node-right--compact': isCompact,
              'TextField-node-right--default': !isCompact,
            })}
          >
            {nodeRight}
          </div>
        )}
        {type === 'password' && (
          <div className="TextField-button">
            <Tooltip content={isMasked ? 'Show password' : 'Hide password'}>
              <Button
                icon={isMasked ? 'watch' : 'watch'}
                onClick={toggleMask}
              />
            </Tooltip>
          </div>
        )}
      </div>
      <InlineError
        error={patternErrorMessageState || error}
        status={warning ? 'warning' : 'danger'}
      />
    </FocusWithin>
  )
}

export default TextField
